import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicLeadsApi } from '../lib/api';
import {
  HEARD_ABOUT_US_OPTIONS,
  MAX_LEAD_NOTE_LENGTH,
  PRIMARY_INTEREST_OPTIONS,
  TITLE_OPTIONS,
  TITLE_OTHER,
  type PublicLeadSource,
} from '../types/lead';

/**
 * Public lead capture (CRM-129, required fields and dropdowns in CRM-133) — the
 * only unauthenticated page that writes.
 *
 * Two variants, one component and one endpoint:
 *   /lead/:repId  internal capture. A rep hands over their phone at a conference;
 *                 attribution comes from the URL (and ?source=<code>), so the coach
 *                 is never asked how they heard about us. Both an email and a phone
 *                 are required here — the rep is standing right there to ask.
 *   /lead         cold capture for the website's request-info link, which does ask,
 *                 and settles for either an email or a phone.
 *
 * Mobile-first and deliberately plain: this is filled in one-handed, on a phone,
 * on venue wifi, by someone who is being talked to at the same time.
 */
const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  state: '',
  zip: '',
  orgName: '',
  sportName: '',
  title: '',
  titleOther: '',
  primaryInterest: '',
  heardAboutUs: '',
  honeypot: '',
};

const inputClasses =
  'w-full rounded-lg border border-gray-300 px-3 py-3 text-base text-gray-900 placeholder-gray-400 ' +
  'focus:border-blast-500 focus:outline-none focus:ring-2 focus:ring-blast-500';

const labelClasses = 'block text-sm font-medium text-gray-700 mb-1';

/** Same shape the backend enforces, so a typo is caught before the round trip. */
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export function PublicLeadForm() {
  const { repId } = useParams<{ repId?: string }>();
  const [searchParams] = useSearchParams();
  const isColdVariant = !repId;

  // A source in the URL is authoritative and stays hidden — the coach should not
  // have to know which conference they are standing at.
  const presetSourceCode = searchParams.get('source')?.trim() || '';

  const [values, setValues] = useState(EMPTY);
  const [sourceCode, setSourceCode] = useState(presetSourceCode);
  const [sources, setSources] = useState<PublicLeadSource[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handed back by the submit response and good once, for the note box below.
  const [noteToken, setNoteToken] = useState('');
  const [note, setNote] = useState('');
  const [noteState, setNoteState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');

  // A bare rep link (no ?source=) gets a picker so a rep can tag the event
  // themselves rather than needing a freshly printed link.
  const needsSourcePicker = !isColdVariant && !presetSourceCode;

  useEffect(() => {
    if (!needsSourcePicker) return;
    publicLeadsApi
      .sources()
      .then(({ leadSources }) => setSources(leadSources))
      .catch(() => setSources([]));
  }, [needsSourcePicker]);

  // "Other" is a prompt, never a stored title: what gets submitted is the text
  // behind it.
  const resolvedTitle = values.title === TITLE_OTHER ? values.titleOther.trim() : values.title;

  const canSubmit = useMemo(() => {
    const filled = (v: string) => v.trim().length > 0;
    const hasContact = isColdVariant
      ? filled(values.email) || filled(values.phone)
      : filled(values.email) && filled(values.phone);
    return (
      filled(values.firstName) &&
      filled(values.lastName) &&
      filled(values.orgName) &&
      filled(resolvedTitle) &&
      ZIP_PATTERN.test(values.zip.trim()) &&
      filled(values.primaryInterest) &&
      hasContact
    );
  }, [values, resolvedTitle, isColdVariant]);

  const set = (key: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const { noteToken: token } = await publicLeadsApi.submit(
        {
          formVersion: 2,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          city: values.city,
          state: values.state,
          zip: values.zip,
          orgName: values.orgName,
          sportName: values.sportName,
          title: resolvedTitle,
          primaryInterest: values.primaryInterest,
          honeypot: values.honeypot,
          // Only the cold form asks this question; the endpoint rejects it otherwise.
          heardAboutUs: isColdVariant ? values.heardAboutUs : '',
          sourceCode,
        },
        repId,
      );
      setNoteToken(token);
      setIsDone(true);
    } catch (err: any) {
      setError(
        err?.response?.status === 429
          ? 'Too many submissions from this network right now. Please try again in a few minutes.'
          : err?.response?.data?.message || 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNote = async () => {
    if (!note.trim() || noteState === 'saving') return;
    setNoteState('saving');
    try {
      const { saved } = await publicLeadsApi.note(noteToken, note.trim());
      setNoteState(saved ? 'saved' : 'failed');
    } catch {
      setNoteState('failed');
    }
  };

  if (isDone) {
    return (
      <main className="min-h-screen bg-gray-50 px-4 py-12">
        <div className="mx-auto max-w-md rounded-xl bg-white p-6 shadow-sm text-center">
          <h1 className="text-xl font-bold text-gray-900">Thanks — we got it</h1>
          <p className="mt-2 text-gray-600">
            Someone from Blast Athletics will follow up with you shortly.
          </p>

          {noteState === 'saved' ? (
            <p className="mt-6 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">Thanks — noted!</p>
          ) : (
            <div className="mt-6 text-left">
              <label className={labelClasses} htmlFor="note">Anything we should know?</label>
              <textarea
                id="note"
                rows={3}
                maxLength={MAX_LEAD_NOTE_LENGTH}
                className={inputClasses}
                placeholder="Optional — e.g. “James is my rep” or “we need this before August”"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              {noteState === 'failed' && (
                <p role="alert" className="mt-2 text-sm text-gray-600">
                  Couldn&apos;t save that — your lead was still received.
                </p>
              )}
              <button
                type="button"
                disabled={!note.trim() || noteState === 'saving'}
                onClick={handleNote}
                className="mt-3 w-full rounded-lg bg-blast-600 px-4 py-2.5 text-sm font-semibold text-white
                           disabled:cursor-not-allowed disabled:opacity-50"
              >
                {noteState === 'saving' ? 'Sending…' : 'Add this note'}
              </button>
            </div>
          )}

          <button
            type="button"
            className="mt-6 text-sm font-medium text-blast-600 underline"
            onClick={() => {
              // The event tag survives on purpose: the next coach at the booth came
              // from the same place.
              setValues(EMPTY);
              setNote('');
              setNoteToken('');
              setNoteState('idle');
              setIsDone(false);
            }}
          >
            Enter another
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-8">
      <form onSubmit={handleSubmit} className="mx-auto max-w-md space-y-4 rounded-xl bg-white p-5 shadow-sm">
        <header>
          <h1 className="text-xl font-bold text-gray-900">Request fundraising info</h1>
          <p className="mt-1 text-sm text-gray-600">
            Tell us who you are, where you coach, and what you&apos;re interested in — we&apos;ll take it from
            there. Fields marked * are required.
          </p>
        </header>

        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        <div>
          <label className={labelClasses} htmlFor="firstName">First name *</label>
          <input id="firstName" className={inputClasses} value={values.firstName} onChange={set('firstName')}
            autoComplete="given-name" required />
        </div>
        <div>
          <label className={labelClasses} htmlFor="lastName">Last name *</label>
          <input id="lastName" className={inputClasses} value={values.lastName} onChange={set('lastName')}
            autoComplete="family-name" required />
        </div>
        <div>
          <label className={labelClasses} htmlFor="email">Email {isColdVariant ? '' : '*'}</label>
          <input id="email" type="email" inputMode="email" className={inputClasses} value={values.email}
            onChange={set('email')} autoComplete="email" required={!isColdVariant} />
        </div>
        <div>
          <label className={labelClasses} htmlFor="phone">Phone {isColdVariant ? '' : '*'}</label>
          <input id="phone" type="tel" inputMode="tel" className={inputClasses} value={values.phone}
            onChange={set('phone')} autoComplete="tel" required={!isColdVariant} />
        </div>
        {isColdVariant && (
          <p className="text-xs text-gray-500">Give us an email or a phone number — either is fine.</p>
        )}
        <div>
          <label className={labelClasses} htmlFor="orgName">School, club, or organization *</label>
          <input id="orgName" className={inputClasses} value={values.orgName} onChange={set('orgName')} required />
        </div>
        <div>
          <label className={labelClasses} htmlFor="sportName">Sport or program</label>
          <input id="sportName" className={inputClasses} value={values.sportName} onChange={set('sportName')} />
        </div>
        <div>
          <label className={labelClasses} htmlFor="title">Your role *</label>
          <select id="title" className={inputClasses} value={values.title} onChange={set('title')} required>
            <option value="">Select one</option>
            {TITLE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
            <option value={TITLE_OTHER}>{TITLE_OTHER}</option>
          </select>
          {values.title === TITLE_OTHER && (
            <input
              id="titleOther"
              aria-label="Your role"
              className={`${inputClasses} mt-2`}
              placeholder="Tell us your role"
              maxLength={100}
              value={values.titleOther}
              onChange={set('titleOther')}
              required
            />
          )}
        </div>
        <div>
          <label className={labelClasses} htmlFor="primaryInterest">What are you interested in? *</label>
          <select id="primaryInterest" className={inputClasses} value={values.primaryInterest}
            onChange={set('primaryInterest')} required>
            <option value="">Select one</option>
            {PRIMARY_INTEREST_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className={labelClasses} htmlFor="city">City</label>
            <input id="city" className={inputClasses} value={values.city} onChange={set('city')}
              autoComplete="address-level2" />
          </div>
          <div>
            <label className={labelClasses} htmlFor="state">State</label>
            <input id="state" className={inputClasses} value={values.state} onChange={set('state')}
              autoComplete="address-level1" />
          </div>
        </div>
        <div>
          <label className={labelClasses} htmlFor="zip">ZIP *</label>
          <input id="zip" inputMode="numeric" className={inputClasses} value={values.zip} onChange={set('zip')}
            autoComplete="postal-code" required />
          {values.zip.trim().length > 0 && !ZIP_PATTERN.test(values.zip.trim()) && (
            <p className="mt-1 text-sm text-gray-600">Enter a 5-digit ZIP code.</p>
          )}
        </div>

        {isColdVariant && (
          <div>
            <label className={labelClasses} htmlFor="heardAboutUs">How did you hear about us?</label>
            <select id="heardAboutUs" className={inputClasses} value={values.heardAboutUs} onChange={set('heardAboutUs')}>
              <option value="">Select one</option>
              {HEARD_ABOUT_US_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        {needsSourcePicker && sources.length > 0 && (
          <div>
            <label className={labelClasses} htmlFor="sourceCode">Event</label>
            <select id="sourceCode" className={inputClasses} value={sourceCode}
              onChange={(e) => setSourceCode(e.target.value)}>
              <option value="">Not at an event</option>
              {sources.map((s) => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Honeypot: hidden from humans, irresistible to bots. A filled value is
            answered with the same thanks page and stored nowhere. */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input id="website" name="website" tabIndex={-1} autoComplete="off" value={values.honeypot}
            onChange={set('honeypot')} />
        </div>

        <button
          type="submit"
          disabled={!canSubmit || isSubmitting}
          className="w-full rounded-lg bg-blast-600 px-4 py-3 text-base font-semibold text-white
                     disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Sending…' : 'Send'}
        </button>
      </form>
    </main>
  );
}
